import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X, Eye, EyeOff } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api, getErr } from "../../lib/api";
import { CATEGORY_OPTIONS, getServiceMeta } from "../../lib/serviceIcons";

const emptyForm = () => ({
  id: "",
  name: "",
  category: "Tax",
  price: "",
  price_label: "",
  tagline: "",
  description: "",
  documents: "",
  is_active: true,
  display_order: 100,
});

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = () => api.get("/admin/services").then((r) => setServices(r.data.services));
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({
      id: s.id,
      name: s.name || "",
      category: s.category || "Tax",
      price: s.price ?? "",
      price_label: s.price_label || "",
      tagline: s.tagline || "",
      description: s.description || "",
      documents: (s.documents || []).join("\n"),
      is_active: s.is_active !== false,
      display_order: s.display_order ?? 100,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (form.price === "" || isNaN(parseFloat(form.price))) return toast.error("Valid price is required");
    const payload = {
      name: form.name.trim(),
      category: form.category,
      price: parseFloat(form.price),
      price_label: form.price_label.trim() || undefined,
      tagline: form.tagline.trim(),
      description: form.description.trim(),
      documents: form.documents.split("\n").map((d) => d.trim()).filter(Boolean),
      is_active: form.is_active,
      display_order: parseInt(form.display_order, 10) || 100,
    };
    try {
      if (editingId) {
        await api.patch(`/admin/services/${editingId}`, payload);
        toast.success("Service updated");
      } else {
        await api.post("/admin/services", payload);
        toast.success("Service created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(getErr(e, "Failed to save service")); }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/admin/services/${s.id}`, { is_active: !s.is_active });
      toast.success(s.is_active ? "Hidden from public site" : "Visible on public site");
      load();
    } catch (e) { toast.error(getErr(e, "Failed")); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove "${s.name}"? If any leads reference it, it will be deactivated instead of deleted.`)) return;
    try {
      const { data } = await api.delete(`/admin/services/${s.id}`);
      toast.success(data.soft_deleted ? "Service deactivated (in use)" : "Service deleted");
      load();
    } catch (e) { toast.error(getErr(e, "Failed")); }
  };

  return (
    <div className="p-4 sm:p-6 md:p-10" data-testid="admin-services-page">
      <Toaster richColors position="top-right" />

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <span className="label-eyebrow">Catalog</span>
          <h1 className="mt-2 text-3xl font-display font-medium tracking-tight text-[#121C17]">Services <span className="text-[#5C6661] text-base ml-1">({services.length})</span></h1>
          <p className="text-sm text-[#5C6661] mt-2 max-w-xl">Manage every service shown on your public site and in the Apply flow. Add, edit, hide and reorder.</p>
        </div>
        <button onClick={startCreate} data-testid="services-create-btn"
          className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2">
          <Plus size={14} /> Add service
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="services-grid">
        {services.map((s) => {
          const { icon: Icon, accent, bgSolid } = getServiceMeta(s);
          return (
            <div key={s.id} className="bento-card p-6 flex flex-col" data-testid={`service-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 rounded-2xl grid place-items-center" style={{ background: bgSolid, color: accent }}>
                  <Icon size={22} />
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full ${s.is_active ? "pill-completed" : "pill-rejected"}`}>{s.is_active ? "Live" : "Hidden"}</span>
              </div>
              <h3 className="mt-4 text-lg font-display font-medium text-[#121C17]">{s.name}</h3>
              <div className="text-sm font-medium mt-0.5" style={{ color: accent }}>{s.price_label}</div>
              <div className="mt-1 text-[11px] uppercase tracking-wider text-[#5C6661]">{s.category} · {(s.documents || []).length} docs</div>
              <p className="mt-3 text-sm text-[#5C6661] flex-1 line-clamp-2">{s.tagline || s.description || ""}</p>
              <div className="mt-5 pt-4 border-t border-[#E5E2DC] flex flex-wrap gap-2">
                <button onClick={() => startEdit(s)} data-testid={`service-edit-${s.id}`}
                  className="rounded-full px-3 py-1.5 text-xs font-medium border border-[#E5E2DC] hover:bg-[#F3F1EC] inline-flex items-center gap-1"><Pencil size={12} /> Edit</button>
                <button onClick={() => toggleActive(s)} data-testid={`service-toggle-${s.id}`}
                  className="rounded-full px-3 py-1.5 text-xs font-medium border border-[#E5E2DC] hover:bg-[#F3F1EC] inline-flex items-center gap-1">
                  {s.is_active ? <><EyeOff size={12} /> Hide</> : <><Eye size={12} /> Show</>}
                </button>
                <button onClick={() => remove(s)} data-testid={`service-delete-${s.id}`}
                  className="ml-auto rounded-full px-3 py-1.5 text-xs font-medium text-red-600 border border-red-100 hover:bg-red-50 inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button>
              </div>
            </div>
          );
        })}
        {services.length === 0 && (
          <div className="col-span-full bento-card p-10 text-center text-sm text-[#5C6661]">No services yet. Click "Add service" to create one.</div>
        )}
      </div>

      {/* Create / Edit dialog (inline panel) */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[#FDFCFB] rounded-3xl max-w-2xl w-full p-6 md:p-8 my-8" data-testid="services-form-dialog">
            <div className="flex items-center justify-between mb-6">
              <div>
                <span className="label-eyebrow">{editingId ? "Edit service" : "New service"}</span>
                <h3 className="mt-1 text-2xl font-display font-medium text-[#121C17]">{editingId ? form.name || "Edit service" : "Add a new service"}</h3>
              </div>
              <button onClick={() => setOpen(false)} className="rounded-full p-2 hover:bg-[#F3F1EC]"><X size={18} /></button>
            </div>

            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Service name *" v={form.name} on={(v) => setForm({ ...form, name: v })} test="srv-form-name" />
                <div>
                  <label className="label-eyebrow text-[10px]">Category</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="srv-form-category"
                    className="mt-2 w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-3 text-sm focus:border-[#1E4D3B]">
                    {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <Field label="Price (₹) *" v={form.price} on={(v) => setForm({ ...form, price: v })} test="srv-form-price" type="number" />
                <Field label="Price label" v={form.price_label} on={(v) => setForm({ ...form, price_label: v })} test="srv-form-price-label" placeholder="Leave blank to auto-format (₹999 etc.)" />
              </div>

              <Field label="Short tagline" v={form.tagline} on={(v) => setForm({ ...form, tagline: v })} test="srv-form-tagline" placeholder="One-line marketing pitch shown on cards" />

              <div>
                <label className="label-eyebrow text-[10px]">Full description</label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="srv-form-description"
                  className="mt-2 w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-3 text-sm focus:border-[#1E4D3B]" />
              </div>

              <div>
                <label className="label-eyebrow text-[10px]">Required documents (one per line)</label>
                <textarea rows={5} value={form.documents} onChange={(e) => setForm({ ...form, documents: e.target.value })} data-testid="srv-form-documents"
                  placeholder={"Aadhaar Card\nPAN Card\nPassport-size Photo"}
                  className="mt-2 w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-3 text-sm focus:border-[#1E4D3B] font-mono" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Display order" v={form.display_order} on={(v) => setForm({ ...form, display_order: v })} test="srv-form-order" type="number" placeholder="Lower = shown first" />
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="srv-form-active">
                    <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 accent-[#1E4D3B]" />
                    <span className="text-sm text-[#121C17]">Visible on public site</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-7 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="rounded-full px-5 py-2.5 text-sm font-medium border border-[#E5E2DC]">Cancel</button>
              <button onClick={submit} data-testid="srv-form-submit"
                className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2">
                <Save size={14} /> {editingId ? "Save changes" : "Create service"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, v, on, test, type = "text", placeholder }) => (
  <div>
    <label className="label-eyebrow text-[10px]">{label}</label>
    <input type={type} value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder} data-testid={test}
      className="mt-2 w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-3 text-sm focus:border-[#1E4D3B]" />
  </div>
);
